import React from "react";
import {
  Drawer,
  Box,
  Typography,
  Stack,
  Chip,
  Divider,
  Paper,
  Alert,
  IconButton,
} from "@mui/material";
import {
  Factory as FactoryIcon,
  Block as BlockIcon,
  Close as CloseIcon,
} from "@mui/icons-material";
import { ProductBreakdownLine, LineType, AdditionalTariff } from "@/services/product-catalogue";
import { useGetDutyColumnsQuery, DutyColumn } from "@/services/product-catalogue/duty-columns-api";
import { Star as StarIcon } from "@mui/icons-material";
import { getAuthorityStyle } from "../rule-form-section/helpers";
import { getCountryFlag } from "@/constants/country-flags";
import { tokens } from "@/tokens";

interface TariffBreakdownPopoverProps {
  open: boolean;
  onClose: () => void;
  productName?: string;
  productSku?: string;
  htsCode?: string;
  countryOfOrigin?: string;
  breakdownLines?: ProductBreakdownLine[];
  additionalTariffs?: AdditionalTariff[];
  totalRate: number;
  metalPercentage?: number;
  ftaApplicable?: boolean;
  ftaProgramName?: string;
  secondaryDutyRateString?: string | null;
}

const getLineTypeLabel = (lineType: LineType): string => {
  switch (lineType) {
    case LineType.METAL:
      return "Metal Portion";
    case LineType.STEEL:
      return "Steel (232)";
    case LineType.ALUMINUM:
      return "Aluminum (232)";
    case LineType.COPPER:
      return "Copper (232)";
    case LineType.NONMETAL:
      return "Non-Metal Portion";
    case LineType.FULL:
      return "Full Value";
    default:
      return lineType;
  }
};

const getLineTypeColors = (lineType: LineType, tokens: any) => {
  switch (lineType) {
    case LineType.METAL:
      return { bg: '#FFF7ED', text: '#C2410C', border: '#FFEDD5' };
    case LineType.STEEL:
      return { bg: '#FEF3C7', text: '#92400E', border: '#FDE68A' };
    case LineType.ALUMINUM:
      return { bg: '#E0E7FF', text: '#3730A3', border: '#C7D2FE' };
    case LineType.COPPER:
      return { bg: '#FFE4E6', text: '#9F1239', border: '#FECDD3' };
    case LineType.NONMETAL:
      return { bg: '#EFF6FF', text: tokens.chip.blue.text, border: tokens.chip.blue.bg };
    default:
      return { bg: tokens.status.successBg, text: tokens.chip.green.text, border: tokens.chip.green.bg };
  }
};


const TariffBreakdownPopover: React.FC<TariffBreakdownPopoverProps> = ({
  open,
  onClose,
  productName,
  productSku,
  htsCode,
  countryOfOrigin,
  breakdownLines,
  additionalTariffs,
  totalRate,
  metalPercentage,
  ftaApplicable,
  ftaProgramName,
  secondaryDutyRateString,
}) => {
  // Show popover if we have either breakdown lines or additional tariffs
  const hasBreakdown = breakdownLines && breakdownLines.length > 0;
  const hasTariffs = additionalTariffs && additionalTariffs.length > 0;
  const { data: dutyColumnsResp } = useGetDutyColumnsQuery({ pageNo: 1, pageSize: 500 } as any);
  const dutyColumns: DutyColumn[] = dutyColumnsResp?.data || [];
  const columnMap = new Map<string, DutyColumn>();
  dutyColumns.forEach(dc => dc && dc._id && columnMap.set(dc._id, dc));
  
  // Check if HTS General Duty is missing
  const hasGeneralDuty = breakdownLines?.some(line => 
    line.calculationBreakdown?.some(calc => 
      calc.applied && (
        calc.columnName.toLowerCase().includes('general duty') ||
        calc.columnName.toLowerCase().includes('hts') ||
        calc.columnId === 'general-duty'
      )
    )
  );
  
  // Collect unique column IDs present in applied breakdown entries (excluding HTS synthetic IDs)
  const usedColumnIds = new Set<string>();
  breakdownLines?.forEach(line => {
    line.calculationBreakdown?.forEach(calc => {
      if (calc.applied && typeof calc.columnId === 'string' && calc.columnId !== 'general-duty' && calc.columnId !== 'secondary-duty') {
        usedColumnIds.add(calc.columnId as string);
      }
    });
  });
  // Build footnotes list: only columns with citations
  const footnoteColumns = Array.from(usedColumnIds)
    .map(id => columnMap.get(id))
    .filter((dc): dc is DutyColumn => !!dc && Array.isArray((dc as any).citations) && (dc as any).citations.length > 0);
  
  if (!hasBreakdown && !hasTariffs) {
    return null;
  }

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          width: { xs: "100vw", sm: 480 },
          maxWidth: "100vw",
          bgcolor: tokens.bg.surface,
        },
      }}
    >
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          px: 3,
          py: 1.5,
          borderBottom: `1px solid ${tokens.border.default}`,
          bgcolor: tokens.bg.hover,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <Box>
            <Typography sx={{ fontWeight: tokens.fontWeight.bold, fontSize: tokens.fontSize.lg, color: tokens.text.primary, lineHeight: 1.3 }}>
              Tariff Calculation Breakdown
            </Typography>
          </Box>
        </Box>
        <Stack direction="row" alignItems="center" spacing={1}>
          <Chip
            label={`Total: ${(totalRate * 100).toFixed(2)}%`}
            color="primary"
            size="small"
            sx={{ fontWeight: tokens.fontWeight.bold }}
          />
          <IconButton onClick={onClose} size="small" sx={{ color: tokens.text.muted }}>
            <CloseIcon sx={{ fontSize: 18 }} />
          </IconButton>
        </Stack>
      </Box>

      {/* Content */}
      <Box sx={{ flex: 1, overflow: "auto", p: 2.5 }}>
      <Stack spacing={2}>

        {/* Product Details */}
        {productName && (
          <Paper
            elevation={0}
            sx={{
              p: 2,
              backgroundColor: tokens.bg.hover,
              border: `1px solid ${tokens.border.light}`,
              borderRadius: tokens.radius.md,
            }}
          >
            <Typography sx={{ fontWeight: tokens.fontWeight.bold, fontSize: tokens.fontSize.lg, color: tokens.text.primary, mb: 0.5 }}>
              {productName}
            </Typography>
            <Stack direction="row" spacing={2} flexWrap="wrap" sx={{ mt: 0.5 }}>
              {productSku && (
                <Typography variant="body2" sx={{ color: tokens.text.secondary, fontSize: 13 }}>
                  <span style={{ fontWeight: tokens.fontWeight.semibold, color: tokens.text.muted }}>SKU:</span> {productSku}
                </Typography>
              )}
              {htsCode && (
                <Typography variant="body2" sx={{ color: tokens.text.secondary, fontSize: 13 }}>
                  <span style={{ fontWeight: tokens.fontWeight.semibold, color: tokens.text.muted }}>HTS:</span> {htsCode}
                </Typography>
              )}
              {countryOfOrigin && (
                <Typography variant="body2" sx={{ color: tokens.text.secondary, fontSize: 13 }}>
                  <span style={{ fontWeight: tokens.fontWeight.semibold, color: tokens.text.muted }}>Origin:</span> {getCountryFlag(countryOfOrigin)} {countryOfOrigin}
                </Typography>
              )}
            </Stack>
          </Paper>
        )}

        {ftaApplicable ? (
          <Alert severity="success" sx={{ py: 0.5 }}>
            <Typography variant="body2">
              Preferential origin applies{ftaProgramName ? ` under ${ftaProgramName}` : ''}. General duty may be exempt.
            </Typography>
          </Alert>
        ) : null}

        {metalPercentage !== undefined && metalPercentage > 0 && (
          <Alert severity="info" icon={<FactoryIcon />} sx={{ py: 0.5 }}>
            <Typography variant="body2">
              Product contains {metalPercentage}% metal content
            </Typography>
          </Alert>
        )}

        {!hasGeneralDuty && (
          <Alert severity="error" sx={{ py: 0.5 }}>
            <Typography variant="body2">
              HTS code not found in HTSUSITC database. Please edit and try a different code.
            </Typography>
          </Alert>
        )}

        <Divider />

        {/* Show breakdown lines if available */}
        {hasBreakdown ? (
          <>
            {/* Breakdown Lines */}
            {breakdownLines.map((line, index) => (
          <Paper
            key={index}
            elevation={0}
            sx={{
              p: 1.5,
              backgroundColor: tokens.bg.hover,
              border: `1px solid ${tokens.border.light}`,
              borderRadius: tokens.radius.md,
            }}
          >
            <Stack spacing={1.5}>
              {/* Line Header */}
              <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 0.5 }}>
                <Stack direction="row" alignItems="center" spacing={1}>
                  <Chip
                    label={getLineTypeLabel(line.lineType) === "Metal Portion" ? "Metal" : getLineTypeLabel(line.lineType) === "Non-Metal Portion" ? "Non-Metal" : getLineTypeLabel(line.lineType)}
                    size="small"
                    sx={{
                      backgroundColor: getLineTypeColors(line.lineType, tokens).bg,
                      color: getLineTypeColors(line.lineType, tokens).text,
                      fontWeight: tokens.fontWeight.bold,
                      borderRadius: '4px',
                      border: '1px solid transparent',
                      borderColor: getLineTypeColors(line.lineType, tokens).border,
                    }}
                  />
                  {line.portion < 1 && (
                    <Typography variant="caption" sx={{ color: tokens.text.secondary }} fontFamily="Inter, sans-serif">
                      ({(line.portion * 100).toFixed(0)}% of value)
                    </Typography>
                  )}
                {line.exclusionsApplied && line.exclusionsApplied.length > 0 && (
                  <StarIcon sx={{ fontSize: 16, color: getAuthorityStyle(line.exclusionsApplied[0]?.authority).color }} />
                )}
                </Stack>
                <Typography variant="body2" fontWeight={tokens.fontWeight.bold} fontFamily="Inter, sans-serif">
                  {(line.totalRate * 100).toFixed(2)}%
                </Typography>
              </Stack>
              <Divider sx={{ my: 1, borderColor: tokens.border.default }} />
              {/* Applicable Rules */}
              {line.applicableRules.length > 0 ? (
                <Box>
                  <Typography variant="caption" fontWeight={tokens.fontWeight.semibold} sx={{ color: tokens.text.secondary, mb: 0.5, display: 'block' }} fontFamily="Inter, sans-serif">APPLIED RULES</Typography>
                  <Box component="ul" sx={{ pl: 2.5, my: 0 }}>
                    {line.applicableRules.map((rule, ruleIndex) => {
                      const excluded = (line.exclusionsApplied || []).some(e => e.columnId === rule.columnId);
                      const color = excluded ? getAuthorityStyle((line.exclusionsApplied || []).find(e => e.columnId === rule.columnId)?.authority).color : undefined;
                      // Render specific duty string for HTS secondary duty
                      if (rule.columnId === 'secondary-duty') {
                        const s = (secondaryDutyRateString || '').toString().trim();
                        if (!s || s.toLowerCase() === 'null') {
                          return null;
                        }
                        return (
                          <li key={ruleIndex}>
                            <Typography variant="body2" fontSize={13} fontFamily="Inter, sans-serif">
                              Specific Duty: {s}
                            </Typography>
                          </li>
                        );
                      }
                      return (
                        <li key={ruleIndex}>
                          <Typography variant="body2" fontSize={13} fontFamily="Inter, sans-serif">
                            {rule.columnName}: {(rule.rate * 100).toFixed(2)}%
                            {excluded && (
                              <>
                                <StarIcon sx={{ fontSize: 14, color, verticalAlign: 'middle', ml: 0.5 }} />
                                <span style={{ marginLeft: 4, color: tokens.status.success, fontWeight: tokens.fontWeight.semibold }}>{"→ 0%"}</span>
                              </>
                            )}
                          </Typography>
                        </li>
                      );
                    })}
                  </Box>
                </Box>
              ) : (
                <Typography variant="caption" sx={{ color: tokens.text.secondary }} fontStyle="italic" fontFamily="Inter, sans-serif">No applicable duties on this portion.</Typography>
              )}

            </Stack>
          </Paper>
            ))}

            {/* Non-applicable duty types intentionally omitted */}

            {/* Exclusion Information */}
            {breakdownLines.some(line => 
              line.calculationBreakdown?.some(calc => calc.mutuallyExclusiveGroup)
            ) && (
              <Alert severity="warning" icon={<BlockIcon />} sx={{ py: 0.5 }}>
                <Typography variant="caption">
                  Some rules belong to mutually exclusive groups - only one rule per group can apply
                </Typography>
              </Alert>
            )}
          </>
        ) : (
          /* Simple tariff list when no breakdown available */
          <Stack spacing={1}>
            <Typography variant="body2" sx={{ color: tokens.text.secondary }}>
              Applied Duty Types:
            </Typography>
            {additionalTariffs && additionalTariffs
              .filter(tariff => tariff.rate > 0 || tariff.columnId === 'secondary-duty')
              .map((tariff, index) => (
                <Paper
                  key={index}
                  elevation={0}
                  sx={{
                    p: 1.5,
                    backgroundColor: tokens.bg.hover,
                    border: `1px solid ${tokens.border.default}`,
                    borderRadius: 1,
                  }}
                >
                  <Stack direction="row" alignItems="center" justifyContent="space-between">
                    <Typography variant="body2">
                      {tariff.columnId === 'secondary-duty' ? 'Specific Duty (HTS)' : tariff.columnName}
                    </Typography>
                    {tariff.columnId === 'secondary-duty' ? (() => {
                      const s = (secondaryDutyRateString || '').toString().trim();
                      if (!s || s.toLowerCase() === 'null') {
                        return null;
                      }
                      return (
                      <Chip
                        label={s}
                        size="small"
                        sx={{
                          backgroundColor: '#e0f2fe',
                          color: '#0369a1',
                          fontWeight: tokens.fontWeight.bold,
                        }}
                      />
                      );
                    })() : (
                    <Chip
                      label={`${(tariff.rate * 100).toFixed(2)}%`}
                      size="small"
                      sx={{
                        backgroundColor: '#e0f2fe',
                        color: '#0369a1',
                        fontWeight: tokens.fontWeight.bold,
                      }}
                    />
                    )}
                  </Stack>
                </Paper>
              ))}
            {additionalTariffs && additionalTariffs.filter(t => t.rate > 0).length === 0 && (
              <Typography variant="body2" sx={{ color: tokens.text.secondary }} fontStyle="italic">
                No duties apply to this product
              </Typography>
            )}
          </Stack>
        )}
        
        {/* Citations Footnotes */}
        {footnoteColumns.length > 0 && (
          <>
            <Divider />
            <Paper elevation={0} sx={{ p: 1.5, border: `1px solid ${tokens.border.default}`, borderRadius: tokens.radius.md, bgcolor: tokens.bg.hover }}>
              <Typography variant="subtitle2" fontSize={12} fontWeight={tokens.fontWeight.heavy} sx={{ mb: 1, textTransform: 'uppercase', color: tokens.text.excerpt }}>Sources</Typography>
              <Stack spacing={1.5}>
                {footnoteColumns.map((dc: any, idx: number) => (
                  <Box key={dc._id}>
                    <Typography variant="body2" fontWeight={tokens.fontWeight.bold} fontSize={13} color={tokens.text.primary}>
                      {idx + 1}. {dc.name}
                    </Typography>
                    <Box component="ul" sx={{ pl: 2.5, my: 0.5 }}>
                      {(dc.citations || []).map((c: any, cidx: number) => {
                        return (
                          <li key={cidx}>
                            <Typography variant="body2" fontSize={13} sx={{ wordBreak: 'break-all' }}>
                              <a href={c.url} target="_blank" rel="noopener noreferrer" style={{ color: tokens.brand.primary, textDecoration: 'none' }}>{c.url}</a>
                            </Typography>
                          </li>
                        );
                      })}
                    </Box>
                  </Box>
                ))}
              </Stack>
            </Paper>
          </>
        )}
        {/* Exclusions Legend */}
        {hasBreakdown && breakdownLines?.some(l => (l.exclusionsApplied || []).length > 0) && (
          <>
            <Divider />
            <Box>
              <Typography variant="subtitle2" fontWeight={tokens.fontWeight.heavy} sx={{ mb: 1 }}>
                Exclusions
              </Typography>
              <Stack spacing={1}>
                {Array.from(
                  new Map(
                    breakdownLines!.flatMap(l => (l.exclusionsApplied || [])).map(e => [e.listId + e.columnId, e])
                  ).values()
                ).map((e, idx) => {
                  const color = getAuthorityStyle(e.authority).color;
                  return (
                    <Stack key={`${e.listId}-${e.columnId}-${idx}`} direction="row" spacing={1} alignItems="center">
                      <StarIcon sx={{ fontSize: 16, color }} />
                      <Typography variant="body2">
                        {e.listName}: {e.columnName} — {e.matchedCode}
                      </Typography>
                    </Stack>
                  )
                })}
              </Stack>
            </Box>
          </>
        )}
      </Stack>
      </Box>
    </Drawer>
  );
};

export default TariffBreakdownPopover;